import { CircleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/lib/auth';
import { useLocale, useT } from '@/lib/i18n';

/** Dedicated login surface — brand lockup, GIS button, no emoji, no chrome. */
export function LoginScreen() {
  const { status, gisReady, renderButton, deniedEmail } = useAuth();
  const { locale } = useLocale();
  const t = useT();
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'unauthenticated' && gisReady && btnRef.current) {
      renderButton(btnRef.current, locale === 'nb' ? 'no' : 'en');
    }
  }, [status, gisReady, renderButton, locale]);

  const busy = status === 'loading' || status === 'verifying';

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-sm text-center">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-brand text-brand-fg">
            <span className="text-title font-bold">N</span>
          </div>
          <div>
            <h1 className="text-title text-fg">{t.loginHeadline()}</h1>
            <p className="mt-1.5 text-body text-fg-muted">{t.loginSub()}</p>
          </div>
        </div>

        <div className="flex min-h-[52px] flex-col items-center justify-center gap-3">
          {busy ? <Spinner className="size-5" /> : null}

          {status === 'unconfigured' ? <Notice tone="warn">{t.notConfigured()}</Notice> : null}

          {status === 'error' ? <Notice tone="warn">{t.errorBody()}</Notice> : null}

          {status === 'denied' ? (
            <Notice tone="bad">{t.loginDenied(deniedEmail ?? '')}</Notice>
          ) : null}

          {/* GIS renders its button into this node when ready */}
          <div ref={btnRef} className="flex justify-center" />
        </div>
      </div>
    </div>
  );
}

function Notice({ tone, children }: { tone: 'warn' | 'bad'; children: ReactNode }) {
  const cls = tone === 'bad' ? 'text-bad-fg' : 'text-warn-fg';
  return (
    <p className={`flex items-center gap-2 text-body ${cls}`}>
      <CircleAlert className="size-4 shrink-0" aria-hidden />
      {children}
    </p>
  );
}
